import {assert, assertAndContinue} from 'Asserts';
import {areActionsEqual} from 'config-migration/import/planning-logic/convertImportIdentifiersToPlans';
import {
  BaseMigrationObjectPlan,
  ChildMigrationObjects,
  PrimaryObjectPlan,
  SecondaryObjectPlan,
} from 'config-migration/types/MigrationObjects';
import {ObjectActionType, UpdateAction} from 'config-migration/types/objectActions';
import {PrimaryObjectType, SecondaryObjectType} from 'config-migration/types/ObjectType';
import {customSchemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';

export const BASE_ROW_PADDING = 42;
export const NESTED_ROW_PADDING_INCREMENT = 13;

const NESTED_ROW_ACTION_DROPDOWN_OFFSET = 16;

export enum NestedCardRowType {
  NonNested = 'NonNested',
  Header = 'Header',
  Regular = 'Regular',
  Footer = 'Footer',
  HeaderAndFooter = 'HeaderAndFooter',
}

export function getNestedRowPadding(nestedDepth: number): number {
  return NESTED_ROW_PADDING_INCREMENT * nestedDepth;
}

/**
 * Counteract the padding we add for nesting level to have the action dropdowns line up
 */
export function getNestedActionDropdownMargin(
  nestedDepth: number,
  nestedCardRowType: NestedCardRowType
): number {
  let margin = -(NESTED_ROW_ACTION_DROPDOWN_OFFSET + NESTED_ROW_PADDING_INCREMENT * nestedDepth);
  if (nestedCardRowType !== NestedCardRowType.NonNested) {
    margin += BASE_ROW_PADDING;
  }
  return margin;
}

/**
 * Map of child object types to their section titles if the title is the same regardless of the parent type.
 */
const CHILD_SECTION_TITLE_MAP = new Map([
  [SecondaryObjectType.DropdownOption, 'Dropdown options'],
  [SecondaryObjectType.ProcessFlowchartConfig, 'Components'],
  [SecondaryObjectType.ProcedureMethodDefinitionVersion, 'Unit operations'],
  [SecondaryObjectType.DATA_PARAMETER, 'Parameters'],
  [SecondaryObjectType.MATERIAL_INPUT, 'Material inputs'],
  [SecondaryObjectType.MATERIAL_OUTPUT, 'Material outputs'],
  [SecondaryObjectType.EQUIPMENT, 'Equipment'],
]);

export function getChildSectionTitleText(
  parentPlan: PrimaryObjectPlan | SecondaryObjectPlan,
  childType: SecondaryObjectType | PrimaryObjectType
): string {
  const parentType = parentPlan.objectType;

  if (CHILD_SECTION_TITLE_MAP.has(childType as SecondaryObjectType)) {
    return CHILD_SECTION_TITLE_MAP.get(childType as SecondaryObjectType)!;
  }

  if (childType === PrimaryObjectType.Template && parentType === PrimaryObjectType.ProcessTaskSchema) {
    return 'Execution template';
  }

  if (childType === SecondaryObjectType.SchemaField) {
    if (parentType === PrimaryObjectType.ProcessOutputSchema) {
      return 'Output schema fields';
    } else if (parentType === PrimaryObjectType.Fieldset) {
      return `${customSchemaFieldsetName()} fields`;
    } else {
      return 'Schema fields';
    }
  }

  assertAndContinue(false, `Unexpected child type ${childType}`);
  return 'Fields';
}

/**
 * For each child, remove the child's selected action and any actions where the target
 * is already archived from the child's additional actions. This is done for surfacing
 * archive actions since they are found in additional actions.
 */
export function getUnselectedUpdateActions(
  childMigrationPlans: Array<SecondaryObjectPlan>
): Array<UpdateAction> {
  return childMigrationPlans
    .map((child) =>
      child.additionalActions.filter(
        (action) => !areActionsEqual(action, child.selectedAction) && !action.target.archived
      )
    )
    .flat();
}

/**
 * Return whether the migration plan has a forced UPDATE action.
 * Currently this is only done when there is a matching provenanceUri.
 */
export function isForcedAutoUpdate(plan: BaseMigrationObjectPlan): boolean {
  return (
    plan.initialAction.type === ObjectActionType.UPDATE &&
    plan.objectId === plan.initialAction.target.provenanceUri
  );
}

/**
 * Return the child migration plans with actionable plans first.
 */
export function getSortedChildMigrationPlans(
  childMigrationPlans: Array<SecondaryObjectPlan>
): Array<SecondaryObjectPlan> {
  const nonActionableChildMigrationPlans = childMigrationPlans.filter((childPlan) =>
    isForcedAutoUpdate(childPlan)
  );
  const actionableChildMigrationPlans = childMigrationPlans.filter(
    (childPlan) => !nonActionableChildMigrationPlans.includes(childPlan)
  );
  return [...actionableChildMigrationPlans, ...nonActionableChildMigrationPlans];
}

/**
 * Returns the parent plan with the updated child plan replacing itself in childMigrationPlans. The original
 * index from childMigrationPlans is used since we sort the child objects before displaying.
 */
export function getPlanForUpdatedChildPlan(
  plan: PrimaryObjectPlan | SecondaryObjectPlan,
  childMigrationObjects: ChildMigrationObjects,
  updatedChildPlan: SecondaryObjectPlan,
  originalIndex: number | undefined
): PrimaryObjectPlan | SecondaryObjectPlan {
  assert(originalIndex !== undefined, "Expected to find child's original index");

  const updatedChildMigrationPlans = [
    ...childMigrationObjects.childMigrationPlans.slice(0, originalIndex),
    updatedChildPlan,
    ...childMigrationObjects.childMigrationPlans.slice(originalIndex + 1),
  ];

  return {
    ...plan,
    childMigrationObjectsByType: new Map(plan.childMigrationObjectsByType).set(updatedChildPlan.objectType, {
      childMigrationPlans: updatedChildMigrationPlans,
      subtractedChildObjects: childMigrationObjects.subtractedChildObjects,
      unplannableChildObjects: childMigrationObjects.unplannableChildObjects,
    }),
  };
}
